export type RecruitmentStatus = 'draft' | 'recruiting' | 'paused' | 'ended' | 'reviewing' | 'awaiting_contract' | 'awaiting_script' | 'producing' | 'completed'
export type ApplicationStatus = 'pending' | 'approved' | 'rejected'

export const commissionRateYuanPerMinute = 1000
export const commissionRateLabel = `${commissionRateYuanPerMinute}元/分钟`

export interface ApplicantVideo {
  title: string
  duration: string
  url?: string
}

export interface CommissionApplication {
  id: string
  name: string
  appliedAt: string
  experience: string
  email: string
  phone: string
  wechat: string
  works: ApplicantVideo[]
  trialWork?: ApplicantVideo
  quotedRateYuanPerMinute?: number
  score?: number
  isWinner?: boolean
  ndaSignedAt?: string
  orderContractUserSignedAt?: string
  orderContractSignedAt?: string
  orderContractFileName?: string
  fullScriptUnlockedAt?: string
  status: ApplicationStatus
  rejectionReason?: string
  identity?: 'personal' | 'company'
  company?: string
}

export interface ProductionCommission {
  id: string
  title: string
  synopsis: string
  sampleExcerpt: string
  scriptFileName: string
  fullScriptFileName?: string
  fullScriptSize?: number
  fullScriptUploadedAt?: string
  submissionPoints?: number
  rateYuanPerMinute?: number
  totalEpisodes?: number
  paidParticipantCount?: number
  winnerApplicationId?: string
  firstPaymentStatus?: 'not_due' | 'due' | 'paid'
  finalPaymentStatus?: 'not_due' | 'due' | 'paid'
  covers: string[]
  recruitmentStartedAt?: string
  recruitmentStartDate?: string
  deadline: string
  status: RecruitmentStatus
  applications: CommissionApplication[]
  applicationCount?: number
  pendingApplicationCount?: number
  revision?: number
}

export const recruitmentLabels: Record<RecruitmentStatus, string> = {
  draft: '草稿',
  recruiting: '招募中',
  paused: '已暂停',
  ended: '已结束',
  reviewing: '评选中',
  awaiting_contract: '待签约',
  awaiting_script: '待解锁剧本',
  producing: '制作中',
  completed: '已完成',
}

export const applicationLabels: Record<ApplicationStatus, string> = {
  pending: '待审核',
  approved: '已通过',
  rejected: '已拒绝',
}

const exampleApplication = (
  id: string,
  name: string,
  appliedAt: string,
  experience: string,
): CommissionApplication => ({
  id,
  name,
  appliedAt,
  experience,
  email: `${id}@example.com`,
  phone: '138****5678',
  wechat: `example_${id}`,
  quotedRateYuanPerMinute: commissionRateYuanPerMinute,
  works: [
    { title: '作品视频 1', duration: '01:24' },
    { title: '作品视频 2', duration: '02:10' },
    { title: '作品视频 3', duration: '00:48' },
  ],
  trialWork: { title: '试稿作品视频', duration: '01:36' },
  status: 'pending',
})

// Standalone prototype records; the admin route reads server data.
export const exampleCommissions: ProductionCommission[] = [
  {
    id: 'example-1',
    title: '《星河归来》',
    synopsis: '近未来科幻题材，讲述人类在星际流浪后重返地球的成长与选择。',
    sampleExcerpt: '第一幕 · 重逢\n舱门缓缓开启，久违的地球重力扑面而来。',
    scriptFileName: '',
    covers: [],
    deadline: '2026-12-31',
    status: 'recruiting',
    applications: [
      exampleApplication('lin', '林小满', '2026-10-12 14:20', '3 部漫剧作品 · 5 年经验'),
      exampleApplication('chen', '陈墨', '2026-10-11 09:15', '2 部漫剧作品 · 3 年经验'),
      exampleApplication('xia', '夏天', '2026-10-10 20:36', '1 部漫剧作品 · 2 年经验'),
    ],
  },
  {
    id: 'example-2',
    title: '《长安夜行》',
    synopsis: '古风悬疑题材，以盛唐长安为背景，讲述一名少年捕快探寻连环谜案的故事。',
    sampleExcerpt: '夜色如墨，长安城的灯火在朱雀大街上连成一片。',
    scriptFileName: '',
    covers: [],
    deadline: '2026-12-31',
    status: 'recruiting',
    applications: [
      exampleApplication('wang', '王若', '2026-10-10 10:10', '2 部漫剧作品 · 4 年经验'),
      exampleApplication('zhou', '周予', '2026-10-09 15:30', '1 部漫剧作品 · 2 年经验'),
    ],
  },
  {
    id: 'example-3',
    title: '《她的第二人生》',
    synopsis: '都市女性成长题材，讲述女主在遭遇人生低谷后，重新出发、找回自我的治愈故事。',
    sampleExcerpt: '她站在落地窗前，俯瞰着这座熟悉又陌生的城市。',
    scriptFileName: '',
    covers: [],
    deadline: '2026-11-30',
    status: 'paused',
    applications: [],
  },
]
